<script setup lang="ts">
import { ref, defineProps, defineEmits, onMounted, onUnmounted, computed, watch } from 'vue';
import { InvoiceItem, DiscountType } from '../types';
import TaxPicker from './TaxPicker.vue';
import { MessageSquareIcon, CopyIcon, TrashIcon } from './Icons.js';

const props = defineProps<{
  item: InvoiceItem;
  currency: string;
  canRemove: boolean;
  gridClassName: string;
}>();

const emit = defineEmits<{
  (e: 'update:item', id: string, field: keyof InvoiceItem, value: any): void;
  (e: 'remove', id: string): void;
  (e: 'duplicate', id: string): void;
}>();

const showDiscountComment = ref(false);
const commentRef = ref<HTMLDivElement | null>(null);

const handleClickOutside = (event: MouseEvent) => {
  if (showDiscountComment.value && commentRef.value && !commentRef.value.contains(event.target as Node)) {
    showDiscountComment.value = false;
  }
};

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
});
onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside);
});

const localPrice = ref('')

const syncFromProps = () => {
	localPrice.value = (props.item.price / 100).toString()
}

onMounted(syncFromProps)

const onPriceInput = (val: string) => {
	const cleaned = val
		.replace(',', '.')
		.replace(/[^0-9.]/g, '')
		.replace(/(\..*)\./g, '$1')

	localPrice.value = cleaned
}

const onPriceBlur = () => {
	const normalized = localPrice.value.replace(',', '.')
	const num = parseFloat(normalized)

	if (!isNaN(num)) {
		const cents = Math.round(num * 100)
		handleUpdate('price', cents)
		localPrice.value = (cents / 100).toFixed(2)
	} else {
		localPrice.value = (props.item.price / 100).toFixed(2)
	}
}

const totalCents = computed(() => {
	const gross = props.item.quantity * props.item.price

	let discountCents = 0

	if (props.item.discount?.value > 0) {
		if (props.item.discount.type === DiscountType.PERCENTAGE) {
			discountCents = Math.round(gross * props.item.discount.value / 100)
		} else {
			discountCents = Math.round(props.item.discount.value * 100)
		}
	}

	return Math.max(0, gross - discountCents)
})

const handleUpdate = (field: keyof InvoiceItem, value: any) => {
    emit('update:item', props.item.id, field, value);
};

const handleDiscountValueChange = (val: number) => {
     const currentType = props.item.discount?.type || DiscountType.PERCENTAGE;
     const currentComment = props.item.discount?.comment || '';
     handleUpdate('discount', { type: currentType, value: val, comment: currentComment });
};

const toggleDiscountType = () => {
      const currentType = props.item.discount?.type || DiscountType.PERCENTAGE;
      const currentValue = props.item.discount?.value || 0;
      const currentComment = props.item.discount?.comment || '';
      const newType = currentType === DiscountType.PERCENTAGE ? DiscountType.FIXED : DiscountType.PERCENTAGE;
      handleUpdate('discount', { type: newType, value: currentValue, comment: currentComment });
};

const handleDiscountCommentChange = (val: string) => {
      const currentType = props.item.discount?.type || DiscountType.PERCENTAGE;
      const currentValue = props.item.discount?.value || 0;
      handleUpdate('discount', { type: currentType, value: currentValue, comment: val });
};

const hasComment = computed(() => props.item.discount?.comment && props.item.discount.comment.trim().length > 0);

watch(
	() => props.item.price,
	() => syncFromProps()
)
</script>

<template>
  <div :class="`group relative items-center px-8 py-4 bg-white hover:bg-slate-50/80 transition-colors ${gridClassName}`">
    <!-- Name -->
    <div>
      <input :value="item.name" @input="handleUpdate('name', ($event.target as HTMLInputElement).value)" class="input-base" :placeholder="$t('invoices.item_name')" />
    </div>
    <!-- Code -->
    <div>
       <input :value="item.code" @input="handleUpdate('code', ($event.target as HTMLInputElement).value)" class="input-base text-center px-1" :placeholder="$t('invoices.code')" />
    </div>
    <!-- Qty -->
    <div>
       <input type="number" :value="item.quantity" @input="handleUpdate('quantity', Number(($event.target as HTMLInputElement).value))" class="input-base text-center px-1" />
    </div>
    <!-- Unit -->
    <div>
       <div class="relative">
         <select :value="item.unit" @change="handleUpdate('unit', ($event.target as HTMLSelectElement).value)" class="input-base px-1 appearance-none">
             <option value="pcs">pc</option>
             <option value="hr">hr</option>
             <option value="kg">kg</option>
         </select>
       </div>
    </div>
    <div>
			<input
				type="text"
				inputmode="decimal"
				:value="localPrice"
				@input="onPriceInput(($event.target as HTMLInputElement).value)"
				@blur="onPriceBlur"
				class="input-base text-right"
			/>
		</div>
		<div class="relative">
			<div class="relative">
				<input
					type="number"
					placeholder="0"
					:value="item.discount?.value ?? ''"
					@input="handleDiscountValueChange(Number(($event.target as HTMLInputElement).value || 0))"
					class="discount-input"
				/>

				<!-- icons INSIDE input -->
				<div class="discount-actions">
					<button
						type="button"
						@click="showDiscountComment = !showDiscountComment"
						:class="[
          'icon-btn',
          hasComment ? 'icon-active' : 'icon-muted'
        ]"
					>
						<MessageSquareIcon class="w-3.5 h-3.5" />
					</button>

					<button
						type="button"
						@click="toggleDiscountType"
						class="icon-type-btn"
					>
						{{ item.discount?.type === DiscountType.FIXED ? currency : '%' }}
					</button>
				</div>
			</div>

			<!-- comment -->
			<div
				v-if="showDiscountComment"
				ref="commentRef"
				class="absolute top-full right-0 z-20 mt-1 w-56 p-2 bg-white rounded-lg shadow-xl border border-slate-200"
			>
    <textarea
			class="w-full h-20 text-xs p-2 border border-slate-200 rounded resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
			:placeholder="$t('invoices.discount_comment_placeholder')"
			:value="item.discount?.comment || ''"
			@input="handleDiscountCommentChange(($event.target as HTMLInputElement).value)"
		/>
			</div>
		</div>



		<!-- TAXES -->
    <div class="flex justify-center">
        <TaxPicker :selectedTaxes="item.taxes || []" @change="(taxes) => handleUpdate('taxes', taxes)" />
    </div>
    <!-- Sum -->
    <div class="flex items-center justify-between pl-2 relative">
          <span class="font-bold text-slate-900 text-sm truncate block w-full text-right">
            {{ (totalCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 }) }}
          </span>
          <div class="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity absolute right-[-20px] top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded shadow-sm border border-slate-100 p-1 z-10">
              <button
                type="button"
                @click="emit('duplicate', item.id)"
                class="p-1.5 text-slate-400 hover:text-blue-500 rounded hover:bg-blue-50"
                title="Duplicate Item"
              >
                  <CopyIcon class="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                @click="emit('remove', item.id)"
                :disabled="!canRemove"
                :class="`p-1.5 rounded transition-colors ${canRemove ? 'text-slate-400 hover:text-red-500 hover:bg-red-50' : 'text-slate-200 cursor-not-allowed'}`"
                title="Remove Item"
              >
                  <TrashIcon class="w-3.5 h-3.5" />
              </button>
          </div>
    </div>
  </div>
</template>

<style scoped>
.input-base {
	display: block;
	height: 36px;
	width: 100%;

	box-sizing: border-box;
	border-radius: 6px;
	border: 1px solid rgb(226,232,240);
	background-color: white;

	padding: 6px 12px;

	font-size: 0.875rem;
	line-height: 1.25rem;

	color: inherit;
}
.input-base::placeholder {
	color: rgb(148,163,184);
}

.input-base:focus-visible {
	outline: 2px solid #3b82f6;
	outline-offset: 0;
}

.input-base:disabled {
	cursor: not-allowed;
	opacity: 0.5;
}

.input-base::file-selector-button {
	border: 0;
	background: transparent;
	font-size: 0.875rem;
	font-weight: 500;
}

.discount-input {
	height: 36px;
	width: 100%;
	border-radius: 6px;
	border: 1px solid rgb(226,232,240);
	background-color: white;
	padding: 6px 64px 6px 12px;
	font-size: 0.875rem;
	text-align: right;
	color: #ef4444;
}

.discount-input:focus-visible {
	outline: 2px solid #3b82f6;
}

.discount-actions {
	position: absolute;
	right: 6px;
	top: 50%;
	transform: translateY(-50%);
	display: flex;
	gap: 4px;
	background: white;
	padding-left: 4px;
}

.icon-btn {
	height: 24px;
	width: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	transition: background 0.15s;
}

.icon-btn:hover {
	background: #f1f5f9;
}

.icon-muted {
	color: #cbd5e1;
}

.icon-active {
	color: #3b82f6;
}

.icon-type-btn {
	height: 24px;
	width: 24px;
	font-size: 10px;
	font-weight: 700;
	border-radius: 4px;
	background: #f1f5f9;
	color: #64748b;
}

.icon-type-btn:hover {
	background: #e2e8f0;
	color: #334155;
}


</style>
